import React from 'react'

type Measured = {
    width: number
    props: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- one ref, two runtimes: a DOM node on web, a View instance on native.
        ref: React.RefObject<any>
        onLayout(event: { nativeEvent: { layout: { width: number } } }): void
    }
}

/**
 * Border-box element width, measured on both platforms. `onLayout` covers native but never
 * fires for the plain DOM nodes NativeWind renders on web, so the web side reads
 * the node directly and follows it with a `ResizeObserver` — without it the
 * width stays 0 in a browser and every width-driven layout silently falls back
 * to its default.
 */
export const useMeasuredWidth = (): Measured => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see Measured.props.ref
    const ref = React.useRef<any>(null)
    const [width, setWidth] = React.useState(0)

    React.useEffect(() => {
        const node = ref.current
        if (typeof ResizeObserver === 'undefined' || !(node instanceof Element)) {
            return
        }
        setWidth(node.getBoundingClientRect().width)
        const observer = new ResizeObserver(entries => {
            // `contentRect` is the CONTENT box; native's `onLayout` and the seed
            // above are both border-box, so reading it would silently shrink the
            // width by padding + border on web only.
            const entry = entries[0]
            const measured =
                entry?.borderBoxSize?.[0]?.inlineSize ?? node.getBoundingClientRect().width
            if (measured) {
                setWidth(measured)
            }
        })
        observer.observe(node)
        return () => observer.disconnect()
    }, [])

    return {
        width,
        props: {
            ref,
            onLayout: event => setWidth(event.nativeEvent.layout.width),
        },
    }
}
